'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEventListener, useMediaQuery } from 'usehooks-ts';
import { useUAContext } from '@/context/UAContext';
import { useEnableFull3d } from '@/hooks/useEnableFull3d';
import { detectInApp } from '@/lib/in-app-browser';
import { SCROLL_LOCK_DELAY_MS, areToolbarsExpanded, isStandaloneDisplayMode, isSwipeFullscreenEligible, isViewportOverlaid, needsScrollRoomReset } from '@/lib/ios-swipe-fullscreen';
export const FULL3D_VIEWPORT_HEIGHT_VAR = '--full3d-viewport-height';
type SavedStyles = {
  htmlOverflow: string;
  htmlOverscroll: string;
  htmlScrollSnap: string;
  htmlScrollbarWidth: string;
  bodyOverflow: string;
  bodyOverscroll: string;
};
export type UseIosSwipeFullscreenReturn = {
  /** Eligible in any orientation: the player is fixed and the document has scroll room. */
  active: boolean;
  showSwipeLayer: boolean;
  /**
   * Layer hidden (portrait, or landscape fullscreen): the player must absorb vertical swipes. iOS Safari
   * moves its toolbars when a swipe in the game iframe chains to the document, even over `overflow: hidden`.
   */
  absorbSwipes: boolean;
  dismiss: () => void;
};

/**
 * iOS Safari has no Element Fullscreen API, so on full3d it lets the user swipe up to scroll the
 * top-level document, which makes Safari collapse its toolbars. See CONNECT-6131.
 *
 * Portrait keeps the toolbars: the document is locked at the top, because Safari brings its toolbars
 * back on any downward swipe in portrait and the collapse/expand cycle made the page jitter. The scroll
 * room stays so Safari usually collapses its toolbars on rotation to landscape (the first rotation after
 * load may not); the swipe layer and toolbar measurement run in landscape only.
 */
export function useIosSwipeFullscreen() {
  const $ = _c(28);
  const enableFull3d = useEnableFull3d();
  const {
    device,
    isMobileApp,
    uaInfo
  } = useUAContext();
  const osName = uaInfo.os.name;
  const t0 = uaInfo.browser.name ?? "";
  let t1;
  if ($[0] !== t0 || $[1] !== uaInfo.ua) {
    t1 = detectInApp(uaInfo.ua, t0);
    $[0] = t0;
    $[1] = uaInfo.ua;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isInAppBrowser = t1;
  const [active, setActive] = useState(false);
  const [toolbarsExpanded, setToolbarsExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const activeRef = useRef(false);
  const lastExpandedRef = useRef(null);
  const rotatedRef = useRef(false);
  const rafRef = useRef(0);
  const savedStylesRef = useRef(null);
  const isLandscape = useMediaQuery("(orientation: landscape)");
  let t2;
  let t3;
  if ($[3] !== device || $[4] !== enableFull3d || $[5] !== isInAppBrowser || $[6] !== isMobileApp || $[7] !== osName) {
    t2 = () => {
      setActive(isSwipeFullscreenEligible({
        enableFull3d,
        device,
        isMobileApp,
        osName,
        isEmbedded: window.self !== window.top,
        isStandalone: isStandaloneDisplayMode(),
        isInAppBrowser
      }));
    };
    t3 = [device, enableFull3d, isInAppBrowser, isMobileApp, osName];
    $[3] = device;
    $[4] = enableFull3d;
    $[5] = isInAppBrowser;
    $[6] = isMobileApp;
    $[7] = osName;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  const landscapeActive = active && isLandscape;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (!activeRef.current) {
        return;
      }
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        document.documentElement.style.setProperty(FULL3D_VIEWPORT_HEIGHT_VAR, `${window.innerHeight}px`);
        const visualViewport = window.visualViewport;
        if (visualViewport && isViewportOverlaid({
          scale: visualViewport.scale,
          visualHeight: visualViewport.height,
          layoutHeight: window.innerHeight
        })) {
          return;
        }
        const expanded = areToolbarsExpanded({
          visibleHeight: visualViewport ? visualViewport.height + visualViewport.offsetTop : window.innerHeight,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height
        });
        if (lastExpandedRef.current !== expanded || rotatedRef.current) {
          setDismissed(false);
        }
        rotatedRef.current = false;
        lastExpandedRef.current = expanded;
        setToolbarsExpanded(expanded);
      });
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const evaluate = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      if (!activeRef.current) {
        return;
      }
      rotatedRef.current = true;
      evaluate();
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleOrientationChange = t5;
  useEventListener("resize", evaluate);
  useEventListener("orientationchange", handleOrientationChange);
  useEventListener("scroll", evaluate);
  let t6;
  let t7;
  if ($[12] !== active) {
    t6 = () => {
      if (!active) {
        return;
      }
      const html = document.documentElement;
      const body = document.body;
      savedStylesRef.current = {
        htmlOverflow: html.style.overflow,
        htmlOverscroll: html.style.overscrollBehavior,
        htmlScrollSnap: html.style.scrollSnapType,
        htmlScrollbarWidth: html.style.scrollbarWidth,
        bodyOverflow: body.style.overflow,
        bodyOverscroll: body.style.overscrollBehavior
      };
      html.style.scrollSnapType = "none";
      html.style.scrollbarWidth = "none";
      html.style.overscrollBehavior = "none";
      body.style.overscrollBehavior = "none";
      return () => {
        const saved = savedStylesRef.current;
        if (saved) {
          html.style.overflow = saved.htmlOverflow;
          html.style.overscrollBehavior = saved.htmlOverscroll;
          html.style.scrollSnapType = saved.htmlScrollSnap;
          html.style.scrollbarWidth = saved.htmlScrollbarWidth;
          body.style.overflow = saved.bodyOverflow;
          body.style.overscrollBehavior = saved.bodyOverscroll;
        }
        savedStylesRef.current = null;
      };
    };
    t7 = [active];
    $[12] = active;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[15] !== landscapeActive) {
    t8 = () => {
      if (!landscapeActive) {
        return;
      }
      activeRef.current = true;
      const visualViewport_0 = window.visualViewport;
      visualViewport_0?.addEventListener("resize", evaluate);
      visualViewport_0?.addEventListener("scroll", evaluate);
      evaluate();
      return () => {
        activeRef.current = false;
        lastExpandedRef.current = null;
        rotatedRef.current = false;
        cancelAnimationFrame(rafRef.current);
        visualViewport_0?.removeEventListener("resize", evaluate);
        visualViewport_0?.removeEventListener("scroll", evaluate);
        document.documentElement.style.removeProperty(FULL3D_VIEWPORT_HEIGHT_VAR);
      };
    };
    t9 = [landscapeActive, evaluate];
    $[15] = landscapeActive;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  useEffect(t8, t9);
  const showSwipeLayer = landscapeActive && toolbarsExpanded && !dismissed;
  let t10;
  let t11;
  if ($[18] !== active || $[19] !== landscapeActive || $[20] !== showSwipeLayer) {
    t10 = () => {
      const saved_0 = savedStylesRef.current;
      if (!active || !saved_0) {
        return;
      }
      const html_0 = document.documentElement;
      const body_0 = document.body;
      if (!landscapeActive) {
        window.scrollTo(0, 0);
        html_0.style.overflow = "hidden";
        body_0.style.overflow = "hidden";
        return;
      }
      if (showSwipeLayer) {
        html_0.style.overflow = saved_0.htmlOverflow;
        body_0.style.overflow = saved_0.bodyOverflow;
        if (needsScrollRoomReset({
          scrollHeight: html_0.scrollHeight,
          viewportHeight: window.innerHeight,
          scrollY: window.scrollY
        })) {
          window.scrollTo(0, 0);
        }
        return;
      }
      const timer = window.setTimeout(() => {
        html_0.style.overflow = "hidden";
        body_0.style.overflow = "hidden";
      }, SCROLL_LOCK_DELAY_MS);
      return () => window.clearTimeout(timer);
    };
    t11 = [active, landscapeActive, showSwipeLayer];
    $[18] = active;
    $[19] = landscapeActive;
    $[20] = showSwipeLayer;
    $[21] = t10;
    $[22] = t11;
  } else {
    t10 = $[21];
    t11 = $[22];
  }
  useEffect(t10, t11);
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setDismissed(true);
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const dismiss = t12;
  const t13 = active && !showSwipeLayer;
  let t14;
  if ($[24] !== active || $[25] !== showSwipeLayer || $[26] !== t13) {
    t14 = {
      active,
      showSwipeLayer,
      absorbSwipes: t13,
      dismiss
    };
    $[24] = active;
    $[25] = showSwipeLayer;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
}